import { useRef } from 'react';
import Container from '@/components/Container';
import SubHeading from '@/components/SubHeading';
import Lead from '@/components/Lead';
import Link from '@/components/Link';
import Text from '@/components/Text';
import Check from '@/components/Check';
import Image from '@/components/Image/RemoteImage';
import ScrollSpy, { ScrollSpyItem } from '@/components/ScrollSpy';
import TrainerTestimonial from '@/domain/Trainer/TrainerTestimonial';
import CoachTrainingAnimation from '@/domain/CoachTraining/CoachTrainingAnimation';
import CoachesTrainingRequestForm from '@/domain/Forms/CoachesTrainingRequestForm';
import * as testimonials from '@/domain/Trainer/testimonials';

export default function TrainersFormation() {
  const competenciesRef = useRef(null);
  const evaluationRef = useRef(null);
  const applyRef = useRef(null);

  const sectionRefs = [competenciesRef, evaluationRef, applyRef];

  return (
    <ScrollSpy offset={-100} sectionRefs={sectionRefs}>
      {({ currentElementIndexInViewport }) => (
        <div className="bg-white">
          <div className="relative z-20 overflow-hidden bg-gradient-to-tr from-violet-300 via-green-100 to-violet-200 lg:py-10">
            <Container className="pt-10 pb-20 lg:py-0">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="relative z-50 flex flex-col space-y-8 py-10 pr-10 lg:col-span-6 lg:py-20 lg:pr-20">
                  <div className="flex flex-col space-y-4">
                    <SubHeading
                      color="text-violet-900"
                      heading="Súmate a nuestro equipo"
                    />
                    <h1 className="mb-4 text-5xl font-bold tracking-tight lg:text-6xl lg:font-extrabold">
                      Fórmate como entrenador
                    </h1>
                    <Lead>
                      Los Official Scrum Trainer (OSTC) son profesionales con
                      licencia para ofrecer entrenamientos y certificaciones de
                      la AIBES.
                    </Lead>
                  </div>
                  <div className="flex items-center">
                    <a
                      className="inline-flex rounded-full bg-violet-800 px-5 py-3 text-lg font-semibold leading-none text-white hover:bg-violet-900 focus:bg-violet-900 focus:ring-4 focus:ring-violet-900 focus:ring-opacity-20"
                      href="#aplicar"
                    >
                      Aplica hoy mismo
                    </a>
                  </div>
                </div>
              </div>
            </Container>
            <CoachTrainingAnimation />
          </div>
          <div
            className="relative top-0 z-30 hidden bg-white shadow-sm md:sticky md:block"
            style={{
              backdropFilter: 'blur(20px) saturate(120%)',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <Container>
              <div className="flex items-center justify-center space-x-10">
                <div className="flex items-center space-x-4 lg:-mb-px lg:space-x-10 xl:space-x-12">
                  <ScrollSpyItem
                    anchor="competencias"
                    label="Competencias"
                    targetClassName="flex items-center border-b-2 border-transparent py-4 lg:py-6 leading-6 font-medium"
                    className={
                      currentElementIndexInViewport === 0
                        ? 'border-green-500 text-green-500'
                        : ''
                    }
                  />
                  <ScrollSpyItem
                    anchor="evaluacion"
                    label="Evaluación y desempeño"
                    targetClassName="flex items-center border-b-2 border-transparent py-4 lg:py-6 leading-6 font-medium"
                    className={
                      currentElementIndexInViewport === 1
                        ? 'border-green-500 text-green-500'
                        : ''
                    }
                  />
                  <ScrollSpyItem
                    anchor="aplicar"
                    label="Aplicar"
                    targetClassName="flex items-center border-b-2 border-transparent py-4 lg:py-6 leading-6 font-medium"
                    className={
                      currentElementIndexInViewport === 2
                        ? 'border-green-500 text-green-500'
                        : ''
                    }
                  />
                </div>
              </div>
            </Container>
          </div>
          <div
            id="competencias"
            className="relative z-10 py-10 lg:py-20"
            ref={competenciesRef}
          >
            <Container className="relative z-10 space-y-10 py-10 lg:space-y-16">
              <div className="flex flex-col items-center space-y-1 text-center">
                <div className="mb-4">
                  <svg
                    className="h-20 w-20"
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                  >
                    <rect
                      className="fill-current text-violet-500"
                      x="19"
                      y="37"
                      width="10"
                      height="5"
                    />
                    <path
                      className="fill-current text-violet-200"
                      d="M31,30c-1.917,1.186-2,3.742-2,6v1H19V36c0-2.258-.118-4.874-2-6a13.355,13.355,0,0,1-6-11.308A12.849,12.849,0,0,1,24,6,12.849,12.849,0,0,1,37,18.692,13.355,13.355,0,0,1,31,30Z"
                    />
                    <path
                      className="fill-current text-violet-400"
                      d="M26,27.633V37H22V27.633A5.982,5.982,0,0,1,18,22h3.6a2.4,2.4,0,0,0,4.8,0H30A5.982,5.982,0,0,1,26,27.633Z"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <SubHeading color="text-violet-700" heading="Competencias" />
                  <h2 className="mb-4 text-3xl font-bold tracking-tight lg:text-4xl">
                    Nuestros entrenadores
                  </h2>
                </div>
              </div>
              <div className="rounded-2xl bg-white">
                <div className="rounded-2xl shadow-2xl">
                  <div className="grid grid-cols-1 space-y-10 p-10 lg:grid-cols-12 lg:gap-8 lg:space-y-0 lg:divide-x lg:p-14">
                    <div className="flex flex-col space-y-4 lg:col-span-4">
                      <Check size="w-8 h-8" className="text-green-400" />
                      <Text>
                        Tienen vasta experiencia y conocimientos demostrados.
                        Son reconocidos referentes en la región por su
                        contribución y desarrollo en procesos de transformación
                        organizacional.
                      </Text>
                    </div>
                    <div className="flex flex-col space-y-4 lg:col-span-4 lg:pl-8">
                      <Check size="w-8 h-8" className="text-green-400" />
                      <Text>
                        Tienen experiencia internacional a través de programas
                        de entrenamientos, eventos, charlas y congresos a nivel
                        internacional que permiten la socialización del
                        conocimiento.
                      </Text>
                    </div>
                    <div className="flex flex-col space-y-4 lg:col-span-4 lg:pl-8">
                      <Check size="w-8 h-8" className="text-green-400" />
                      <Text>
                        Contribuyen con las empresas para la adopción de la
                        cultura ágil a través de métodos profesionales,
                        innovadores y con resultados demostrados.
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-t from-violet-900 to-violet-800 p-4 font-medium text-violet-200 shadow-lg lg:px-20 lg:py-10">
                <p className="p-4 text-center leading-6 lg:p-0">
                  Todos los OSTC utilizan el mismo material base estandarizado
                  por AIBES, pero son libres de hacer adaptaciones para
                  diferentes entornos y países, en todos los casos, aportan su
                  propio estilo y experiencia a sus entrenamientos. El uso de
                  materiales preparados de forma central por AIBES, para el cual
                  se toma en cuenta la experiencia y el conocimiento de todos
                  los profesionales que la componen, garantiza que todos los
                  estudiantes de sus entrenamientos adquieren el mismo nivel de
                  preparación, independientemente del Entrenador, el
                  entrenamiento o el lugar donde se imparta.
                </p>
              </div>
              <TrainerTestimonial testimonial={testimonials.federicoVargas} />
            </Container>
            <div className="absolute top-0 z-0 -mt-20 h-full w-full -skew-y-6 transform bg-white bg-gradient-to-b from-white to-green-100" />
          </div>
          <div
            id="evaluacion"
            className="relative py-10 lg:pt-20 lg:pb-32"
            ref={evaluationRef}
          >
            <Container className="relative z-10 space-y-10 pt-10 lg:space-y-20">
              <div className="flex flex-col items-center space-y-1 text-center">
                <div className="mb-4">
                  <svg
                    className="h-20 w-20"
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                  >
                    <rect
                      className="fill-current text-violet-500"
                      x="19"
                      y="37"
                      width="10"
                      height="5"
                    />
                    <path
                      className="fill-current text-violet-200"
                      d="M31,30c-1.917,1.186-2,3.742-2,6v1H19V36c0-2.258-.118-4.874-2-6a13.355,13.355,0,0,1-6-11.308A12.849,12.849,0,0,1,24,6,12.849,12.849,0,0,1,37,18.692,13.355,13.355,0,0,1,31,30Z"
                    />
                    <path
                      className="fill-current text-violet-400"
                      d="M26,27.633V37H22V27.633A5.982,5.982,0,0,1,18,22h3.6a2.4,2.4,0,0,0,4.8,0H30A5.982,5.982,0,0,1,26,27.633Z"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <SubHeading
                    color="text-violet-700"
                    heading="Evaluación y desempeño"
                  />
                  <h2 className="mb-4 text-3xl font-bold tracking-tight lg:text-4xl">
                    Calidad de las capacitaciones
                  </h2>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-20 lg:grid-cols-12 lg:items-center">
                <div className="relative space-y-4 lg:col-span-6 lg:py-20">
                  <div className="flex flex-col space-y-4 md:flex-row md:items-start md:space-y-0 md:space-x-4">
                    <div className="flex flex-1 flex-col space-y-4 lg:space-y-6">
                      <h3 className="text-xl font-bold tracking-tight lg:w-3/4 lg:text-3xl xl:text-4xl">
                        Evaluación y mejoramiento continuo
                      </h3>
                      <div className="text-lg leading-8 text-gray-600 lg:pr-20">
                        Nuestros entrenadores son evaluados y mejoran su
                        desempeño a través de la retroalimentación que reciben
                        de sus estudiantes.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex lg:col-span-6">
                  <div className="relative w-full">
                    <div className="aspect-w-16 aspect-h-10 relative z-50 overflow-hidden rounded shadow-lg lg:rounded-lg lg:shadow-xl xl:rounded-xl">
                      <div className="absolute inset-0">
                        <Image
                          src="v1616985273/formacion/entrenadores-feedback_w2d165.jpg"
                          alt="Retroalimentación de entrenadores"
                          layout="fill"
                          objectFit="cover"
                        />
                      </div>
                    </div>
                    <div className="absolute top-0 left-0 -mt-4 -ml-4 h-14 w-2/4 rounded bg-violet-500 lg:rounded-lg" />
                    <div className="absolute bottom-0 right-0 -mr-4 mb-10 h-16 w-2/5 rounded bg-violet-500 lg:rounded-lg" />
                    <div className="absolute bottom-0 left-0 z-50 -ml-10 mb-10 h-16 w-44 rounded border-2 border-violet-500 lg:rounded-lg" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-20 lg:grid-cols-12 lg:items-center">
                <div className="relative space-y-4 lg:order-2 lg:col-span-6 lg:py-20">
                  <div className="flex flex-col space-y-4">
                    <div className="flex flex-1 flex-col space-y-4 lg:space-y-6">
                      <h3 className="text-xl font-bold tracking-tight lg:w-2/4 lg:text-3xl xl:text-4xl">
                        Cultura de retroalimentación
                      </h3>
                      <div className="text-lg leading-8 text-gray-600 lg:pr-20">
                        Anualmente se reúnen para compartir sus experiencias y
                        mejorar en conjunto los materiales de cada curso
                        impartido por ellos, así como los materiales que son
                        utilizados en cada entrenamiento.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex lg:order-1 lg:col-span-6">
                  <div className="flex w-full rounded-lg bg-gray-200 lg:rounded-xl xl:rounded-2xl">
                    <div className="relative w-full">
                      <div className="aspect-w-16 aspect-h-10 relative z-50 overflow-hidden rounded shadow-lg lg:rounded-lg lg:shadow-xl xl:rounded-xl">
                        <div className="absolute inset-0">
                          <Image
                            src="v1616985273/formacion/formacion-entrenadores-scrum_zrr6it.jpg"
                            alt="Encuentro anual de entrenadores"
                            layout="fill"
                            objectFit="cover"
                          />
                        </div>
                      </div>
                      <div className="absolute top-0 left-0 -mt-6 -ml-20 h-14 w-2/4 rounded bg-violet-500 lg:rounded-lg" />
                      <div className="absolute bottom-0 right-0 -mr-4 mb-10 h-16 w-2/5 rounded bg-violet-500 lg:rounded-lg" />
                      <div className="absolute bottom-0 left-0 z-50 -ml-4 mb-10 h-16 w-44 rounded border-2 border-violet-500 lg:rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-20 lg:grid-cols-12 lg:items-center">
                <div className="relative space-y-4 lg:col-span-6 lg:py-20">
                  <div className="flex flex-col space-y-4">
                    <div className="flex flex-1 flex-col space-y-4 lg:space-y-6">
                      <h3 className="text-xl font-bold tracking-tight lg:text-3xl xl:text-4xl">
                        Calidad en la enseñanza
                      </h3>
                      <div className="text-lg leading-8 text-gray-600 lg:pr-20">
                        Los materiales de los cursos impartidos por nuestros
                        entrenadores son los de más alta calidad, consistencia y
                        profesionalismo. El prestigio y el reconocimiento de sus
                        entrenadores y certificaciones dan fe de ello.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex lg:col-span-6">
                  <div className="flex w-full rounded-lg bg-gray-200 lg:rounded-xl xl:rounded-2xl">
                    <div className="relative w-full">
                      <div className="aspect-w-16 aspect-h-10 relative z-50 overflow-hidden rounded shadow-lg lg:rounded-lg lg:shadow-xl xl:rounded-xl">
                        <div className="absolute inset-0">
                          <Image
                            src="v1616985273/formacion/material-de-entrenadores_lbnyhz.jpg"
                            alt="Retroalimentación de entrenadores"
                            layout="fill"
                            objectFit="cover"
                          />
                        </div>
                      </div>
                      <div className="absolute top-0 left-0 -mt-6 -ml-10 h-14 w-2/4 rounded bg-violet-500 lg:rounded-lg" />
                      <div className="absolute bottom-0 right-0 -mr-4 mb-10 h-16 w-2/5 rounded bg-violet-500 lg:rounded-lg" />
                      <div className="absolute bottom-0 left-0 z-50 -ml-4 mb-10 h-16 w-44 rounded border-2 border-violet-500 lg:rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
            </Container>
            <div className="absolute top-0 z-0 -mt-20 h-full w-full -skew-y-6 transform bg-white" />
          </div>
          <Container className="lg:-mt-20">
            <TrainerTestimonial testimonial={testimonials.erickMasgo} />
          </Container>
          <div id="aplicar" className="relative" ref={applyRef}>
            <div className="pt-20 lg:pt-32">
              <Container className="relative z-10 space-y-10 lg:space-y-20 lg:py-10">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                  <div className="space-y-4 text-white lg:col-span-6 lg:space-y-8 lg:pt-5 lg:pr-10">
                    <div className="flex flex-col">
                      <SubHeading color="text-violet-700" heading="Aplicar" />
                      <h2 className="text-3xl font-semibold tracking-tight lg:text-4xl">
                        Inicia tu camino como Entrenador
                      </h2>
                    </div>
                    <p className="text-lg text-gray-400">
                      Aplica hoy mismo y comienza el proceso de formación como
                      entrenador de AIBES.
                    </p>
                  </div>
                  <div className="space-y-6 md:space-y-4 lg:col-span-6">
                    <CoachesTrainingRequestForm />
                    <p className="px-4 text-xs leading-normal text-gray-600">
                      Cualquier información que usted nos pueda proporcionar
                      permanece privada y no es compartida con ningún fin
                      comercial con terceros. Para mayor información revise
                      nuestras&nbsp;
                      <Link
                        className="font-medium text-gray-500"
                        href="/legal/privacidad"
                      >
                        Políticas de privacidad
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </Container>
            </div>
            <div className="h-20 bg-gray-900" />
            <div className="absolute top-0 z-0 grid h-full w-full -skew-y-6 transform grid-cols-12 grid-rows-6 bg-gray-900">
              <div className="col-span-3 col-start-10 -mt-10 h-10 bg-violet-500" />
              <div className="col-span-1 row-start-1 h-10 bg-gray-800" />
              <div className="col-span-1 col-start-2 row-start-1 -mt-10 h-10 border" />
            </div>
          </div>
        </div>
      )}
    </ScrollSpy>
  );
}

TrainersFormation.layoutProps = {
  stickyHeader: false,
};
